import { ViewBooking } from "collins-js-sdk";
import React, { useCallback, useEffect, useRef, useState } from "react";
import PullToRefresh from "react-simple-pull-to-refresh";
import { addOrUpdateBooking } from "../../../../APIs/Booking/UpdateBooking";
import { useAppContext } from "../../../../hooks/useAppContext";
import { useDiaryContext } from "../../../../hooks/useDiaryContext";
import { Spinner } from "../../../ui/spinner";
import { RunsheetBookingList } from "./components/RunsheetBookingList";
import { RunsheetEmptyState } from "./components/RunsheetEmptyState";
import { RunsheetToolbar } from "./components/RunsheetToolbar";
import { useBookingFilter } from "./hooks/useBookingFilter";
import { useBookingGrouping } from "./hooks/useBookingGrouping";
import { useBookingSearch } from "./hooks/useBookingSearch";
import { useBookingStats } from "./hooks/useBookingStats";
// Icons are now used in extracted components

type SortOrder = "none" | "asc" | "desc";

interface RunsheetBetaProps {
  sortedBookings?: ViewBooking[];
  sortOrder?: SortOrder;
  onSortToggle?: () => void;
  onSearch?: (query: string) => void;
}

export const RunsheetBeta = ({
  sortedBookings = [],
  sortOrder = "none",
  onSortToggle,
  onSearch
}: RunsheetBetaProps) => {
  const diaryContext = useDiaryContext();
  const { isInitialising, venueConfig } = diaryContext;
  const { venueGroupConfig } = useAppContext();

  // State management
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [disableSwipe, setDisableSwipe] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [activePopoverBookingId, setActivePopoverBookingId] = useState<
    string | null
  >(null);
  const activePopoverBookingIdRef = useRef(activePopoverBookingId);
  activePopoverBookingIdRef.current = activePopoverBookingId;

  // Search functionality
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (onSearch) {
      onSearch(query);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    if (onSearch) {
      onSearch("");
    }
  };

  // Helper function to render the main content area
  const renderMainContent = () => {
    if (isInitialising) {
      return (
        <div className="RunSheet_Beta_Loading flex flex-col items-center justify-center px-5 py-10 gap-4 min-h-[60vh] w-full text-center">
          <Spinner className="size-8" />
          <p className="text-muted-foreground text-sm font-medium m-0 text-center">
            Loading bookings...
          </p>
        </div>
      );
    }

    if (Object.keys(groupedBookings).length === 0) {
      return (
        <RunsheetEmptyState
          searchQuery={searchQuery}
          activeFilter={activeFilter}
          onClearSearch={handleClearSearch}
        />
      );
    }

    return (
      <RunsheetBookingList
        groupedBookings={groupedBookings}
        expandedGroups={expandedGroups}
        onExpandedGroupsChange={setExpandedGroups}
        createItemData={createItemData}
      />
    );
  };
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);

  // Use runsheetConfig from DiaryContext
  const { runsheetConfig } = diaryContext;
  const { showTimeDividers } = runsheetConfig;

  // Enhanced stats calculation using custom hook
  const stats = useBookingStats(sortedBookings);

  // Search functionality using custom hook
  const searchResults = useBookingSearch(sortedBookings, searchQuery);

  // Filter bookings using custom hook
  const filteredBookings = useBookingFilter(
    sortedBookings,
    activeFilter,
    searchQuery,
    searchResults,
    venueConfig,
    venueGroupConfig
  );

  // Group bookings by time using custom hook
  const groupedBookings = useBookingGrouping(
    filteredBookings,
    sortOrder,
    showTimeDividers
  );

  // Update expanded groups when groupedBookings change (e.g., after filtering/searching)
  useEffect(() => {
    setExpandedGroups(new Set(Object.keys(groupedBookings)));
  }, [groupedBookings]);

  // Handlers
  const handleBookingChanged = useCallback(
    (booking: ViewBooking) => {
      addOrUpdateBooking({ booking, diaryContext });
    },
    [diaryContext]
  );

  const handleRefresh = useCallback(async () => {
    await diaryContext.fetchBookingList?.();
  }, [diaryContext.fetchBookingList]);

  // Create item data for cards - matching the existing runsheet structure
  const createItemData = useCallback(
    (singleBooking: ViewBooking) => ({
      bookings: [singleBooking], // Pass only the specific booking for this card
      venueConfig,
      venueGroupConfig,
      onBookingChanged: handleBookingChanged,
      selectedView: "RunsheetTab" as const,
      selectedBooking: diaryContext.selectedBooking,
      setSelectedBooking: (selectedBooking: ViewBooking) => {
        diaryContext.setDiaryContext({
          selectedBooking,
          selectedBookingV4: undefined
        });
      },
      refreshBookings: handleRefresh,
      isOnRunSheet: true,
      disableSwipe,
      setDisableSwipe,
      showDetailedLabels: true, // Enable detailed labels for RunsheetBeta
      activePopoverBookingId,
      activePopoverBookingIdRef,
      setActivePopoverBookingId
    }),
    [
      venueConfig,
      venueGroupConfig,
      handleBookingChanged,
      diaryContext,
      handleRefresh,
      disableSwipe,
      activePopoverBookingId
    ]
  );

  return (
    <div className="RunSheet_Container RunSheet_Beta flex flex-col w-full h-full static overflow-x-visible overflow-y-hidden">
      {/* Bookings List - Now contains everything in scrollable area */}
      <div
        className="RunSheet_Beta_BookingsList flex-1 overflow-y-auto overflow-x-hidden h-full [-webkit-overflow-scrolling:touch]"
        data-runsheet-scroll-container
      >
        <PullToRefresh
          onRefresh={handleRefresh}
          refreshingContent={
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                width: "100%"
              }}
            >
              <Spinner className="size-6" />
            </div>
          }
          pullingContent=""
          resistance={2}
        >
          <div className="RunSheet_Beta_Content flex-1 flex flex-col overflow-visible w-full">
            {/* Condensed Toolbar - Single Row 44px */}
            <RunsheetToolbar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onClearSearch={handleClearSearch}
              activeFilter={activeFilter}
              onFilterChange={setActiveFilter}
              isFilterPopoverOpen={isFilterPopoverOpen}
              onFilterPopoverChange={setIsFilterPopoverOpen}
              sortOrder={sortOrder}
              onSortToggle={onSortToggle || (() => {})}
              stats={stats}
              venueConfig={venueConfig}
              venueGroupConfig={venueGroupConfig}
            />
            {renderMainContent()}
          </div>
        </PullToRefresh>
      </div>
    </div>
  );
};

export default RunsheetBeta;
